import { Domain } from "../../swagger";
import { domainApi } from "../../utils/configuration";
import { ChevronDown, ChevronRight, FolderTree, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const MySwal = withReactContent(Swal);

export default function DomainsPage() {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState<Set<number>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [formType, setFormType] = useState<"domain" | "category" | "subcategory">("domain");
  const [parentId, setParentId] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ title: string }>();

  const loadDomains = async () => {
    const data = await domainApi.getAllDomains();
    setDomains(data);
  };

  useEffect(() => {
    loadDomains();
  }, []);

  const handleAdd = (type: typeof formType, parent?: number) => {
    setFormType(type);
    setParentId(parent ?? 0);
    reset();
    setDialogOpen(true);
  };

  const onSubmit = async ({ title }: { title: string }) => {
    try {
      if (formType === "domain") {
        await domainApi.addDomain({ body: { title } });
      } else if (formType === "category") {
        await domainApi.addCategory({ body: { domainId: Number(parentId), title } });
      } else {
        await domainApi.addSubcategory({ body: { categoryId: Number(parentId), title } });
      }

      await MySwal.fire({
        icon: "success",
        title: `${formType} added successfully`,
        timer: 1500,
        showConfirmButton: false,
      });

      setDialogOpen(false);
      loadDomains();
    } catch (err: any) {
      await MySwal.fire({
        icon: "error",
        title: "Failed to add",
        text: err.message || "",
      });
    }
  };

  const confirmDelete = async () => {
    return MySwal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
  };

  const handleDelete = async (id: number, type: typeof formType) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;

    try {
      if (type === "domain") {
        await domainApi.deleteDomain({ id });
      } else if (type === "category") {
        await domainApi.deleteCategory({ id });
      } else {
        await domainApi.deleteSubcategory({ id });
      }

      await MySwal.fire({
        icon: "success",
        title: `${type} deleted successfully`,
        timer: 1500,
        showConfirmButton: false,
      });

      loadDomains();
    } catch (err: any) {
      await MySwal.fire({
        icon: "error",
        title: "Failed to delete",
        text: err.message ?? "",
      });
    }
  };

  const toggleExpand = (set: React.Dispatch<React.SetStateAction<Set<number>>>, id: number) => {
    set((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  return (
    <div className="lg:pl-64 pt-20 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Domain Management</h1>
          <p className="text-gray-600 mt-1">Manage job domains, categories, and subcategories</p>
        </div>
        <Button onClick={() => handleAdd("domain")} className="bg-indigo-600 hover:bg-indigo-700 text-white">
          <Plus className="h-4 w-4 mr-2" /> Add Domain
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FolderTree className="h-5 w-5 mr-2 text-indigo-600" /> Hierarchy Structure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {domains.map((domain) => (
              <div key={domain.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpand(setExpandedDomains, domain.id!)}
                      className="p-1 h-6 w-6"
                    >
                      {expandedDomains.has(domain.id!) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                    <span className="font-semibold text-indigo-700">{domain.title}</span>
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">Domain</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAdd("category", domain.id)}
                      className="h-8 w-8 p-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(domain.id!, "domain")}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {expandedDomains.has(domain.id!) && (
                  <div className="ml-6 mt-3 space-y-2">
                    {domain.Categories?.map((category) => (
                      <div key={category.id} className="border-l-2 border-gray-200 pl-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleExpand(setExpandedCategories, category.id!)}
                              className="p-1 h-6 w-6"
                            >
                              {expandedCategories.has(category.id!) ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                            <span className="font-medium text-gray-700">{category.title}</span>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Category</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAdd("subcategory", category.id)}
                              className="h-8 w-8 p-0"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(category.id!, "category")}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {expandedCategories.has(category.id!) && (
                          <div className="ml-6 mt-2 space-y-1">
                            {category.SubCategories?.map((sub) => (
                              <div key={sub.id} className="flex items-center justify-between py-1">
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-600">{sub.title}</span>
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                    Subcategory
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(sub.id!, "subcategory")}
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {formType}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="title">Name</Label>
              <Input id="title" {...register("title", { required: true })} placeholder="Enter name" />
              {errors.title && <p className="text-red-500 text-sm">This field is required</p>}
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 text-white hover:bg-indigo-700">
                <Plus className="h-4 w-4 mr-1" />Create
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
